/**
 * Copyright (c) 2019 CRF Box Oy. All Rights Reserved. Company Confidential.
 */

import React from 'react';
import PropTypes from 'prop-types';
import Notification from '../../common/notification/notification.component';
import Loading from '../../../components/loading/loading.component';
import cloneDeep from 'lodash/cloneDeep';
import classnames from 'classnames';
import RequestService from '../../../service/requestService';
import * as consts from '../../../const';
import { Grid, MultipleSelectDropdown } from 'tc-client-common';
import StudyIcon from '../../../assets/images/icons/Study-icon.svg';

import { COUNTRIES } from '../../../constants/configCountries';
const COUNTRY_OPTIONS = [{ label: 'All countries', value: 'all' }].concat(
  COUNTRIES.map(({ text: label, value }) => ({ label, value }))
);

const columns = {
  protocol: consts.PROTOCOL_NO,
  study: consts.STUDY,
  role: consts.ROLE,
  site: consts.CLINICAL_SITE,
  accessToSite: consts.DL_COLUMN_SITE,
  accessToCountry: consts.DL_COLUMN_COUNTRY_ACCESS,
  seePhi: consts.CAN_VIEW_PHI,
  obtainConsent: consts.CAN_OBTAIN_PHI
};

class PeopleDetailsGridComponent extends React.Component {
  componentDidMount() {
    this.props.loadData(this.props.user.userId, this.props.selectedSponsor.databasename);

    // used for breadcrumb in SSO redirect
    const { history, location, match } = this.props;
    const { organizationId, organizationName } = location.state.forBreadcrumb;

    if (!organizationId || !organizationName) {
      const { organizationId, organizationName } = match.params;
      const { user, forBreadcrumb } = location.state;

      history.push(location.pathname, {
        user,
        forBreadcrumb: { ...forBreadcrumb, organizationId, organizationName }
      });
    }
  }

  componentWillUnmount() {
    this.props.clearErrors();
  }

  bannerLabel = `${consts.ROLE_MGMT_ONGOING_STUDIES} ${this.props.selectedSponsor.sponsorname}`;

  columnDefinition = [
    { label: columns.study, size: 25 },
    { label: columns.protocol, size: 10 },
    { label: columns.role, size: 25 },
    { label: columns.accessToSite, size: 10 },
    { label: columns.accessToCountry, size: 15 },
    { label: columns.seePhi, textAlignment: 'center', size: 10 },
    { label: columns.obtainConsent, textAlignment: 'center', size: 10 }
  ];

  updateCanViewPhi(rowData) {
    let updatedRowData = cloneDeep(rowData);
    updatedRowData.canViewPhi = !rowData.canViewPhi;

    return this.updateAssignment(updatedRowData);
  }

  updateCanConsent(rowData) {
    let updatedRowData = cloneDeep(rowData);
    updatedRowData.canObtainIc = !rowData.canObtainIc;
    updatedRowData.canObtainIc = !rowData.canObtainIc;

    return this.updateAssignment(updatedRowData);
  }

  updateAssignment(updatedRowData) {
    return () => {
      this.props.updatingCanConsentAndPhi();

      RequestService.updateCanConsentAndPhi(updatedRowData)
        .then((responseData) => {
          this.props.updateCanConsentAndPhi(responseData);
        })
        .catch((err) => {
          this.props.updatingCanConsentAndPhiFailed(updatedRowData);
        });
    };
  }

  getRowsData(userAssignments) {
    const phiCheckbox = (rowData, editable) => () => {
      const readonly = rowData.canObtainIc || !editable;
      const checked = rowData.canViewPhi;

      return readonly && !checked ? (
        ''
      ) : (
        <span
          onClick={readonly ? () => {} : this.updateCanViewPhi(rowData)}
          className={classnames('phi-check', { readonly, checked })}
        />
      );
    };

    const canConsentCheckbox = (rowData, editable) => () => {
      const readonly = !editable;
      const checked = rowData.canObtainIc;

      return readonly && !checked ? (
        ''
      ) : (
        <span
          onClick={readonly ? () => {} : this.updateCanConsent(rowData)}
          className={classnames('consent-check', { readonly, checked })}
        />
      );
    };

    const studyColumnData = (studyName) => () => (
      <span className="study-column">
        <img className="study-icon" src={StudyIcon} />
        <span>{studyName}</span>
      </span>
    );

    const accessToCountry = (countries) => () => {
      return !countries || (countries && countries.length === 0) ? (
        <div className="country-select-field">{consts.NOT_AVAILABLE}</div>
      ) : (
        <div className="country-select-field">
          <MultipleSelectDropdown
            maxHeight={140}
            selectedOptions={countries}
            options={COUNTRY_OPTIONS}
            readonly
            activeLabel
            labels={{
              all: consts.COUNTRY_SELECTOR_LABELS.ALL,
              none: consts.COUNTRY_SELECTOR_LABELS.NONE,
              name: consts.COUNTRY_SELECTOR_LABELS.NAME,
              empty: consts.COUNTRY_SELECTOR_LABELS.EMPTY
            }}
          />
        </div>
      );
    };

    return userAssignments.map((data) => {
      const studyName = data.study.name || '';
      const protocolNumber = data.study.protocolNumber || '';
      const userRole = data.userRole.roleName || '';
      const roleNameDescription = data.userRole.roleNameDescription || userRole;
      const organizationName = data.organization.name || '';
      const rowId = data.assignmentId;
      const countries = data.countries;

      const canEdit = (data.permissions || []).includes(consts.SAVE_ADDITIONAL_ROLES);
      const canEditPhi = canEdit && userRole === consts.SPONSOR_MONITOR;
      const canEditObtainIc = canEdit && userRole === consts.SITE_USER;

      return {
        id: rowId,
        columns: [
          { columnData: studyColumnData(studyName), id: `${rowId}-1` },
          { columnData: protocolNumber, id: `${rowId}-2` },
          { columnData: roleNameDescription, id: `${rowId}-3` },
          { columnData: organizationName, id: `${rowId}-4` },
          { columnData: accessToCountry(countries), id: `${rowId}-5` },
          { columnData: phiCheckbox(data, canEditPhi || canEditObtainIc), id: `${rowId}-6` },
          { columnData: canConsentCheckbox(data, canEditObtainIc), id: `${rowId}-7` }
        ]
      };
    });
  }

  render() {
    const peopleData =
      this.props.userAssignments &&
      this.props.userAssignments.data &&
      this.props.userAssignments.data.assignments &&
      this.props.userAssignments.data.assignments.data;

    if (this.props.isFetching) {
      return <Loading />;
    }
    if (this.props.error) {
      return <Notification type="error" data={this.props.error} />;
    }
    if (!peopleData || !peopleData.length) {
      return <Notification type="info" data="No role assignments found." />;
    }

    return (
      <div className="people-details-grid-component">
        {this.props.isUpdatingPhiConsent && <Loading />}
        <Grid
          gridId="user-management-details-grid"
          disabled={this.props.isUpdatingPhiConsent}
          bannerLabel={this.bannerLabel}
          columnDefinition={this.columnDefinition}
          rows={this.getRowsData(peopleData)}
        />
      </div>
    );
  }
}

PeopleDetailsGridComponent.propTypes = {
  user: PropTypes.object.isRequired,
  error: PropTypes.string,
  clearErrors: PropTypes.func,
  userAssignments: PropTypes.object,
  isFetching: PropTypes.bool,
  selectedSponsor: PropTypes.object,
  loadData: PropTypes.func,
  updatingCanConsentAndPhi: PropTypes.func,
  updateCanConsentAndPhi: PropTypes.func,
  updatingCanConsentAndPhiFailed: PropTypes.func,
  isUpdatingPhiConsent: PropTypes.bool,
  match: PropTypes.object,
  location: PropTypes.object,
  history: PropTypes.object
};

export default PeopleDetailsGridComponent;
